/**
 * Hostname detection utility for multi-subdomain architecture.
 * 
 * Returns the app context based on the current hostname:
 * - 'admin' for admin.idseat.com (internal IDSeat admin + club admin)
 * - 'fan' for go.idseat.com (fan-facing pages)
 * - 'development' for localhost/preview URLs (shows all routes)
 */

export type AppContext = 'admin' | 'fan' | 'development';

export function getAppContext(): AppContext {
  if (typeof window === 'undefined') {
    return 'development';
  }

  const hostname = window.location.hostname;

  // Production subdomains
  if (hostname === 'admin.idseat.com') {
    return 'admin';
  }
  if (hostname === 'go.idseat.com') {
    return 'fan';
  }

  // Development/preview environments - show all routes
  // Includes localhost, *.lovable.app, *.vercel.app, and idseat.com
  if (
    hostname === 'localhost' ||
    hostname.endsWith('.lovable.app') ||
    hostname.endsWith('.vercel.app') ||
    hostname.includes('staging')
  ) {
    return 'development';
  }

  return 'development';
}

/**
 * Check if we're in a development/preview environment
 */
export function isDevelopment(): boolean {
  return getAppContext() === 'development';
}

/**
 * Get the base URL for fan-facing pages (for generating URLs in admin)
 */
export function getFanBaseUrl(): string {
  if (typeof window === 'undefined') {
    return 'https://go.idseat.com';
  }

  const context = getAppContext();

  // In development, use the same origin with /t/ path for backward compatibility
  if (context === 'development') {
    return `${window.location.origin}/t`;
  }

  // In production, fan URLs are on go.idseat.com
  return 'https://go.idseat.com';
}

/**
 * Generate a fan page URL for a specific stadium and tag
 * Uses stadium slug for URL generation (stadium-centric URLs for multi-club stadiums)
 */
export function generateFanPageUrl(stadiumSlug: string, tagPublicId: string): string {
  const context = getAppContext();

  // In development, use the legacy /t/:tagPublicId route for backward compatibility
  if (context === 'development') {
    return `${window.location.origin}/t/${tagPublicId}`;
  }

  // In production, use stadium-centric structure: go.idseat.com/:stadiumSlug/:tagPublicId
  return `https://go.idseat.com/${stadiumSlug}/${tagPublicId}`;
}
